import { List as ImmutableList } from 'immutable';
import { DateTime } from 'luxon';
import { createSelector } from 'reselect';

import { accountsSelectors } from '../accounts';
import { categoriesSelectors } from '../categories';
import { chartOfAccountsUtils } from '../chart-of-accounts';
import { dateSelectors } from '../date';
import { featureFlagsSelectors } from '../feature-flags';
import { scheduledTransactionsSelectors } from '../scheduled-transactions';
import { transactionsSelectors, transactionsUtils } from '../transactions';

import { LruObjectCache, createCachedSelector } from 're-reselect';
import { sortTransactions } from './transactionListUtils';

const isSpendingCat = (coa) =>
  !chartOfAccountsUtils.isTransferCOA(coa) && !categoriesSelectors.isIncomeCat(null, coa?.id);

// returns Immutable List of Transactions
export const getRecentTransactions = createSelector(
  (state) =>
    accountsSelectors.getLoadPending(state) ||
    categoriesSelectors.getLoadPending(state) ||
    transactionsSelectors.getLoadPending(state),
  transactionsSelectors.getTransactionsById,
  categoriesSelectors.getCategoriesById,
  accountsSelectors.getResourcesById,
  dateSelectors.getCurrentDate,
  (state) => featureFlagsSelectors.getFeatureFlag(state, 'exposeInvestmentTransactions') || 'HIDE',
  (state) => featureFlagsSelectors.getFeatureFlags(state).get('hideConnectedLoanTransactions'),
  (
    isLoadPending,
    txnList,
    categoriesById,
    accountsById,
    today,
    exposeInvestmentTransactions,
    hideConnectedLoanTransactions,
  ) => {
    if (isLoadPending) {
      return ImmutableList([]);
    }
    const todayString = today.toISODate();
    const fourDaysAgo = today.minus({ days: 4 }).toISODate();

    const recentTxns = txnList.filter((txn) => {
      if (
        txn.postedOn <= todayString &&
        txn.postedOn > fourDaysAgo && // recent
        !transactionsUtils.isUnacceptedScheduledTxn(txn) &&
        transactionsUtils.isExposedTransaction(txn, accountsById, exposeInvestmentTransactions) &&
        !(hideConnectedLoanTransactions && transactionsUtils.isConnectedLoanTransaction(txn, accountsById))
      ) {
        if (transactionsUtils.isSplitTxn(txn)) {
          // accept any split with a spending item
          let hasExpense = false;
          txn.split.items.forEach((item) => {
            if (isSpendingCat(item.coa)) {
              hasExpense = true;
            }
          });
          return hasExpense;
        } else {
          return isSpendingCat(txn.coa);
        }
      }
      return false;
    });

    return recentTxns
      .sort((txn1, txn2) => {
        if (txn1.postedOn > txn2.postedOn) {
          // sort by date
          return -1;
        }
        if (txn1.postedOn === txn2.postedOn) {
          // if date equal, sort large expense amount to the top
          return txn1.amount < txn2.amount ? -1 : 1;
        }
        return 1;
      })
      ?.toList();
  },
);

function txnIsClose(txn1, txn2) {
  const sameSign = txn1?.amount < 0 === txn2?.amount < 0;
  const reasonableDate = DateTime.fromISO(txn1?.postedOn).diff(DateTime.fromISO(txn2?.postedOn), 'days')?.days;
  let sameType = true;
  if (txn2?.coa?.type === 'ACCOUNT') {
    sameType = txn2.coa.type === 'ACCOUNT' && txn1?.coa?.type === 'ACCOUNT';
  }
  return sameSign && sameType && Math.abs(reasonableDate) < 60;
}
/*
 * GetSortedTransactionsForAccountIds
 */
// THIS IS USED FOR THE MATCHING TXN LIST BOX
// filter out scheduled, matched, and user-entered transactions, then sort by date
export const getSortedTransactionsForAccountIds = createSelector(
  (state, props) => props.matchTxn,
  transactionsSelectors.getTransactionsByTextFilter,
  (matchTxn, transactionsForAccountIds) =>
    sortTransactions(
      transactionsForAccountIds.filter(
        (txn) =>
          txn &&
          txn.source !== 'SCHEDULED_TRANSACTION_PENDING' &&
          txn.source !== 'SCHEDULED_TRANSACTION' &&
          !txn.stModelId &&
          txn.source !== 'USER_ENTERED' &&
          txnIsClose(txn, matchTxn),
      ),
    ),
);

/*
 * GetLesserBilledTransactions
 */
export const getLesserBilledTransactions = createCachedSelector(
  (state, props) => props.isBill,
  (state, props) => props.isTransfer,
  scheduledTransactionsSelectors.getScheduledTransactions,
  transactionsSelectors.getTransactionsByTextFilter,
  categoriesSelectors.getIncomeCOAIds,
  (isBill, isTransfer, scheduledTxns, txnList, incomeCOAs) => {
    const endDate = DateTime.local();
    const startDateISO = endDate.minus({ months: 6 }).toISODate();
    const endDateISO = endDate.toISODate();
    const txns = txnList.filter((txn) => {
      let keepTransaction = true;
      const isIncome = transactionsUtils.isIncomeFromTxn(txn, incomeCOAs);
      if (txn.type !== 'CASH_FLOW') {
        keepTransaction = false;
      } else if (txn.stModelId && scheduledTxns && scheduledTxns.has(txn.stModelId)) {
        keepTransaction = false;
      } else if (isBill !== undefined && (isBill ? isIncome : !isIncome)) {
        keepTransaction = false;
        // If type is not ACCOUNT or it is not an external transfer or if it is not a credit card payment
      } else if (isTransfer === true && txn?.coa?.type !== 'ACCOUNT' && txn?.coa?.id !== '2' && txn?.coa?.id !== '3') {
        keepTransaction = false;
      } else {
        keepTransaction = txn.postedOn >= startDateISO && txn.postedOn <= endDateISO;
      }
      return keepTransaction;
    });

    return sortTransactions(txns);
  },
)({
  keySelector: (_state, props) =>
    `${props?.bill ? 'is_bill' : 'not_bill'}/${props?.isTransfer ? 'is_transfer' : 'not_transfer'}`, // upcoming bills and upcoming income are both using selector at the same time so we need the cache to keep them from clobbering. also including isTransfer just in case
  cacheObject: new LruObjectCache({ cacheSize: 4 }),
});

const upcomingSortBy = 'date';
const upcomingSortOrder = 'ascending';
export const getUpcomingTransactionsForAccountIds = createSelector(
  (state) =>
    transactionsSelectors.getTransactionsOfTypes(
      state,
      transactionsSelectors.txnTypesDefault | transactionsSelectors.TxnTypes.REFUND_VIRTUAL_TXN,
    ),
  (state, props) => props.accountIds, // can be array or immutable, as long as it has 'includes' method
  (state, props) => props.endDate, // expects ISODate string
  dateSelectors.getCurrentDateString,
  (transactions, accountIds, endDate, today) => {
    const filteredTxns = transactions.filter(
      (txn) =>
        accountIds.includes(txn.accountId) &&
        transactionsUtils.isUpcoming(txn, today) &&
        (!endDate || txn.postedOn <= endDate),
    );

    return sortTransactions(filteredTxns, upcomingSortBy, upcomingSortOrder);
  },
);
